import { Component, ElementRef, HostListener, Injector, OnDestroy, OnInit, inject, input, signal, viewChild, ChangeDetectorRef } from '@angular/core';
import { NgIf } from '@angular/common';
import { TiptapEditorDirective, AngularNodeViewRenderer } from 'ngx-tiptap';
import { Editor, Node, mergeAttributes, type Attributes } from '@tiptap/core';
import { Plugin } from 'prosemirror-state';
import { MatDialog } from '@angular/material/dialog';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { StarterKit } from '@tiptap/starter-kit';
import { Image } from '@tiptap/extension-image';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { Link } from '@tiptap/extension-link';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Highlight } from '@tiptap/extension-highlight';
import { FieldState } from '@angular/forms/signals';
import { ImageResizeComponent } from './image-resize.component';
import { ColorPaletteComponent } from './color-palette.component';
import { TableGridPickerComponent } from './table-grid-picker.component';
import { EquationEditorDialogComponent } from './equation-editor-dialog.component';
import { LinkEditorDialogComponent } from './link-editor-dialog.component';
import { MatIconModule } from '@angular/material/icon';

declare const MathJax: any;

const MathInline = Node.create({
  name: 'mathInline',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      latex: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-math]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes({ 'data-math': '' }, HTMLAttributes)];
  },

  addNodeView() {
    return ({ node }: any) => {
      const dom = document.createElement('span');
      dom.setAttribute('data-math', '');
      dom.classList.add('mce-math-node');
      dom.setAttribute('contenteditable', 'false');

      const render = (latex: string) => {
        dom.textContent = `\\(${latex}\\)`;
        if (typeof MathJax !== 'undefined' && MathJax.typesetPromise) {
          MathJax.typesetPromise([dom]).catch(() => {});
        }
      };
      render(node.attrs['latex'] ?? '');

      return {
        dom,
        update: (updatedNode: any) => {
          if (updatedNode.type.name !== 'mathInline') return false;
          render(updatedNode.attrs['latex'] ?? '');
          return true;
        },
      };
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handleDoubleClickOn: (view, pos, node) => {
            if (node.type.name !== 'mathInline') return false;
            (this.editor as any)._openEquationEdit?.(node.attrs['latex'], pos);
            return true;
          },
        },
      }),
    ];
  },
});

// Captions are edited in a node view, but exported as ordinary report HTML.
const imageElement = (element: HTMLElement): HTMLElement =>
  element.matches('figure[data-mce-image]') ? element.querySelector('img')! : element;

const ResizableImage = (injector: Injector) => Image.extend({
  draggable: true,
  addAttributes() {
    const parentAttributes: Attributes = this.parent?.() ?? {};
    return {
      ...Object.fromEntries(Object.entries(parentAttributes).map(([name, attribute]) => [
        name,
        {
          ...attribute,
          parseHTML: (element: HTMLElement) => attribute.parseHTML
            ? attribute.parseHTML(imageElement(element))
            : imageElement(element).getAttribute(name),
        },
      ])),
      width: {
        default: null,
        parseHTML: el => {
          const image = imageElement(el);
          const width = image.style.width || image.getAttribute('width');
          // Legacy HTML width attributes are pixel counts, while CSS requires a unit.
          return width && /^\d+$/.test(width) ? `${width}px` : width || null;
        },
        renderHTML: attrs => attrs.width ? { style: `width: ${attrs.width}` } : {},
      },
      layout: {
        default: 'inline',
        parseHTML: el => imageElement(el).getAttribute('data-layout') ?? 'inline',
        renderHTML: attrs => ({ 'data-layout': attrs['layout'] }),
      },
      showCaption: {
        default: false,
        parseHTML: el => imageElement(el).getAttribute('data-show-caption') === 'true',
        renderHTML: attrs => ({ 'data-show-caption': attrs['showCaption'] ? 'true' : 'false' }),
      },
      caption: {
        default: '',
        parseHTML: el => imageElement(el).getAttribute('data-caption') ?? '',
        renderHTML: attrs => attrs['caption'] ? { 'data-caption': attrs['caption'] } : {},
      },
    };
  },
  parseHTML() {
    const imageSelector = this.options.allowBase64 ? 'img[src]' : 'img[src]:not([src^="data:"])';
    return [
      {
        tag: 'figure[data-mce-image]',
        // Parse the entire figure as one leaf node, avoiding a duplicate caption paragraph.
        getAttrs: (element: HTMLElement) => element.querySelector(imageSelector) ? null : false,
      },
      ...(this.parent?.() ?? []),
    ];
  },
  renderHTML({ node, HTMLAttributes }) {
    const attributes = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes);
    if (node.attrs['showCaption'] && node.attrs['caption'].trim()) {
      return ['figure', { 'data-mce-image': '' },
        ['img', attributes],
        ['figcaption', {}, node.attrs['caption']],
      ];
    }
    return ['img', attributes];
  },
  addNodeView() {
    return AngularNodeViewRenderer(ImageResizeComponent, { injector });
  },
});

@Component({
  selector: 'mce-editor',
  standalone: true,
  imports: [NgIf, TiptapEditorDirective, MatIconModule, ColorPaletteComponent, TableGridPickerComponent],
  templateUrl: './mce-editor.component.html',
  styleUrl: './mce-editor.component.scss'
})
export class MceEditorComponent implements OnInit, OnDestroy {

  private static readonly maxImageFileSize = 1024 * 1024;
  private static readonly maxImageWidth = 2000;
  private static readonly maxImageHeight = 2000;
  private static readonly supportedImageTypes = ['image/jpeg', 'image/png', 'image/webp'];

  readonly field = input.required<FieldState<string>>();
  readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

  private readonly injector = inject(Injector);
  private readonly dialog = inject(MatDialog);
  private readonly changeDetector = inject(ChangeDetectorRef);

  editor!: Editor;
  readonly currentColor = signal<string | null>(null);
  readonly currentHighlight = signal<string | null>(null);
  readonly showTablePicker = signal(false);
  readonly isInTable = signal(false);
  readonly isEquationSelected = signal(false);
  readonly showMoreMenu = signal(false);
  readonly imageUploadError = signal<string | null>(null);

  ngOnInit() {
    this.editor = new Editor({
      extensions: [
        StarterKit.configure({ link: false, underline: false }),
        Underline,
        Subscript,
        Superscript,
        Link.configure({ openOnClick: false }),
        TextStyle,
        Color,
        Highlight.configure({ multicolor: true }),
        ResizableImage(this.injector).configure({ allowBase64: true }),
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        Table.configure({ resizable: true }),
        TableRow,
        TableCell,
        TableHeader,
        MathInline,
      ],
      editorProps: {
        attributes: {
          role: 'textbox',
          spellcheck: 'true',
          'aria-label': 'Summary and comments',
          'aria-multiline': 'true',
        },
      },
      content: this.field().value() ?? '',
      onCreate: ({ editor }) => {
        this.syncToolbarState(editor);
        this.typesetEditorMath(editor.view.dom);
      },
      onUpdate: ({ editor }) => {
        this.field().controlValue.set(editor.getHTML());
        this.typesetEditorMath(editor.view.dom);
      },
      onTransaction: ({ editor }) => {
        this.syncToolbarState(editor);
        // Stored-mark changes can leave both the document and selection unchanged.
        this.changeDetector.markForCheck();
      },
    });
    (this.editor as any)._openEquationEdit = (latex: string, pos: number) => {
      this.openEquationDialog(latex, pos);
    };
  }

  insertEquation() {
    this.openEquationDialog('', null);
  }

  editEquation() {
    const { from } = this.editor.state.selection;
    const node = this.editor.state.doc.nodeAt(from);
    if (node?.type.name === 'mathInline') {
      this.openEquationDialog(node.attrs['latex'], from);
    }
  }

  private openEquationDialog(existing: string, pos: number | null) {
    const ref = this.dialog.open(EquationEditorDialogComponent, {
      width: '540px',
      data: { latex: existing },
    });
    ref.afterClosed().subscribe((latex: string | null) => {
      if (!latex) return;
      if (pos !== null) {
        const { state, view } = this.editor;
        const node = state.doc.nodeAt(pos);
        if (node?.type.name === 'mathInline') {
          const tr = state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, latex });
          view.dispatch(tr);
          this.editor.commands.focus();
        }
      } else {
        this.editor.chain().focus()
          .insertContent({ type: 'mathInline', attrs: { latex } })
          .run();
      }
    });
  }

  private syncToolbarState(editor: Editor) {
    this.isInTable.set(editor.isActive('table'));
    this.isEquationSelected.set(editor.isActive('mathInline'));
    const color = editor.getAttributes('textStyle')['color'] ?? null;
    this.currentColor.set(color);
    const highlight = editor.getAttributes('highlight')['color'] ?? null;
    this.currentHighlight.set(highlight);
  }

  private typesetEditorMath(dom: HTMLElement) {
    if (typeof MathJax === 'undefined' || !MathJax.typesetPromise) return;
    MathJax.typesetPromise([dom]).catch((err: unknown) => console.warn('MathJax typeset error:', err));
  }

  ngOnDestroy() {
    this.editor.destroy();
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.showTablePicker.set(false);
    this.showMoreMenu.set(false);
  }

  toggleMoreMenu(event: MouseEvent) {
    event.stopPropagation();
    this.showMoreMenu.update(v => !v);
  }

  toggleTablePicker(event: MouseEvent) {
    event.stopPropagation();
    this.showTablePicker.update(v => !v);
  }

  insertTable(rows: number, cols: number) {
    this.showTablePicker.set(false);
    this.editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
  }

  addRowBefore() { this.editor.chain().focus().addRowBefore().run(); }
  addRowAfter() { this.editor.chain().focus().addRowAfter().run(); }
  deleteRow() { this.editor.chain().focus().deleteRow().run(); }
  addColumnBefore() { this.editor.chain().focus().addColumnBefore().run(); }
  addColumnAfter() { this.editor.chain().focus().addColumnAfter().run(); }
  deleteColumn() { this.editor.chain().focus().deleteColumn().run(); }
  mergeCells() { this.editor.chain().focus().mergeCells().run(); }
  splitCell() { this.editor.chain().focus().splitCell().run(); }
  toggleHeaderRow() { this.editor.chain().focus().toggleHeaderRow().run(); }
  toggleHeaderColumn() { this.editor.chain().focus().toggleHeaderColumn().run(); }
  deleteTable() { this.editor.chain().focus().deleteTable().run(); }

  setColor(color: string | null) {
    if (color) {
      this.editor.chain().focus().setColor(color).run();
    } else {
      this.editor.chain().focus().unsetColor().run();
    }
    this.currentColor.set(color);
  }

  setHighlight(color: string | null) {
    if (color) {
      this.editor.chain().focus().setHighlight({ color }).run();
    } else {
      this.editor.chain().focus().unsetHighlight().run();
    }
    this.currentHighlight.set(color);
  }

  insertLink() {
    const { from, to } = this.editor.state.selection;
    const currentHref = this.editor.getAttributes('link')['href'] as string | undefined;
    const ref = this.dialog.open(LinkEditorDialogComponent, {
      width: '540px',
      data: { href: currentHref ?? '' },
    });

    ref.afterClosed().subscribe((url: string | null) => {
      const trimmedUrl = url?.trim();
      if (!trimmedUrl) {
        return;
      }

      const href = this.normalizeLinkUrl(trimmedUrl);

      this.editor.chain()
        .focus()
        .setTextSelection({ from, to })
        .extendMarkRange('link')
        .setLink({ href })
        .run();
    });
  }

  private normalizeLinkUrl(url: string): string {
    if (/^(https?:|mailto:|tel:|ftp:|#|\/|\.\/|\.\.\/)/i.test(url)) {
      return url;
    }

    return `https://${url}`;
  }

  clearFormatting() {
    this.editor.chain().focus().clearNodes().unsetAllMarks().run();
  }

  insertImage() {
    const url = prompt('Enter image URL:');
    if (url) {
      this.editor.chain().focus().setImage({ src: url }).run();
    }
  }

  openImageUpload() {
    this.imageUploadError.set(null);
    this.showMoreMenu.set(false);
    this.fileInput().nativeElement.click();
  }

  insertImageFromFile(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) return;

    if (!MceEditorComponent.supportedImageTypes.includes(file.type)) {
      this.resetImageInput(input);
      this.imageUploadError.set('Only JPEG, PNG, and WebP images are supported.');
      return;
    }

    if (file.size > MceEditorComponent.maxImageFileSize) {
      this.resetImageInput(input);
      this.imageUploadError.set('Image must be 1 MB or smaller.');
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const image = document.createElement('img');
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);

      if (image.naturalWidth > MceEditorComponent.maxImageWidth || image.naturalHeight > MceEditorComponent.maxImageHeight) {
        this.resetImageInput(input);
        this.imageUploadError.set('Image dimensions must be 2000×2000 pixels or smaller.');
        return;
      }

      this.imageUploadError.set(null);

      const reader = new FileReader();
      reader.onload = () => {
        this.editor.chain().focus().setImage({ src: reader.result as string }).run();
        this.resetImageInput(input);
      };
      reader.onerror = () => {
        this.resetImageInput(input);
        this.imageUploadError.set('The selected image could not be read.');
      };
      reader.readAsDataURL(file);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      this.resetImageInput(input);
      this.imageUploadError.set('The selected file is not a valid image.');
    };
    image.src = objectUrl;
  }

  private resetImageInput(input: HTMLInputElement | null) {
    if (input) {
      input.value = '';
    }
  }

  setAlign(align: string) {
    this.editor.chain().focus().setTextAlign(align).run();
  }

  isActive(type: string | Record<string, unknown>, attrs?: Record<string, unknown>): boolean {
    if (typeof type === 'string') {
      return this.editor?.isActive(type, attrs) ?? false;
    }
    return this.editor?.isActive(type) ?? false;
  }

  activeBlockType(): string {
    if (!this.editor) return 'paragraph';
    if (this.editor.isActive('heading', { level: 1 })) return 'h1';
    if (this.editor.isActive('heading', { level: 2 })) return 'h2';
    if (this.editor.isActive('heading', { level: 3 })) return 'h3';
    return 'paragraph';
  }

  setBlockType(value: string) {
    switch (value) {
      case 'h1': this.editor.chain().focus().setHeading({ level: 1 }).run(); break;
      case 'h2': this.editor.chain().focus().setHeading({ level: 2 }).run(); break;
      case 'h3': this.editor.chain().focus().setHeading({ level: 3 }).run(); break;
      default:   this.editor.chain().focus().setParagraph().run(); break;
    }
  }
}
